import { DOCUMENT_KIND, DOCUMENT_STATE } from './documents';
import type { Doc, DocumentKind, DocumentState } from './types';

/**
 * FILTER AND SORT FOR THE DOCUMENTS SCREEN — the same idea the backlog runs, so
 * the two feel like one product. A document is filtered by what KIND it is, its
 * STATUS, and what it COVERS; sorted newest/oldest, by status, by kind or by
 * title. All pure, so it is tested without a screen.
 */

export type DocSort = 'newest' | 'oldest' | 'status' | 'kind' | 'title';
export const DOC_SORT_LABEL: Record<DocSort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  status: 'By status',
  kind: 'By kind',
  title: 'Title A–Z',
};

export type DocFilterField = 'kind' | 'status' | 'covers';
export const DOC_FIELD_LABEL: Record<DocFilterField, string> = {
  kind: 'Kind',
  status: 'Status',
  covers: 'What it covers',
};

export type DocFilter = { kind: string[]; status: string[]; covers: string[] };
export const EMPTY_DOC_FILTER: DocFilter = { kind: [], status: [], covers: [] };

/** How many FIELDS are narrowed — the number the Filter button wears. */
export const docFilterCount = (f: DocFilter): number =>
  (f.kind.length ? 1 : 0) + (f.status.length ? 1 : 0) + (f.covers.length ? 1 : 0);

/** The whole project, as a filter value — a document with no sprint covers it. */
export const COVERS_WHOLE = '__whole__';

/** The value a document holds for a field, so a choice can be matched or counted. */
export const docValue = (doc: Doc, field: DocFilterField): string =>
  field === 'kind' ? doc.kind : field === 'status' ? doc.state : doc.sprint || COVERS_WHOLE;

export function docPasses(doc: Doc, f: DocFilter): boolean {
  return (
    (f.kind.length === 0 || f.kind.includes(doc.kind)) &&
    (f.status.length === 0 || f.status.includes(doc.state)) &&
    (f.covers.length === 0 || f.covers.includes(doc.sprint || COVERS_WHOLE))
  );
}

export function toggleDocFilter(f: DocFilter, field: DocFilterField, value: string): DocFilter {
  const cur = f[field];
  return { ...f, [field]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
}

/* Awaiting a signature first, then drafts still being written, then the settled
   ones — the order a person works them in. Ties break newest-first. */
const STATE_ORDER: Record<DocumentState, number> = { awaiting: 0, draft: 1, signed: 2, superseded: 3 };

export function sortDocs(docs: Doc[], sort: DocSort): Doc[] {
  const byNew = (a: Doc, b: Doc) => (b.createdAt ?? 0) - (a.createdAt ?? 0);
  const xs = [...docs];
  switch (sort) {
    case 'newest':
      return xs.sort(byNew);
    case 'oldest':
      return xs.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
    case 'status':
      return xs.sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || byNew(a, b));
    case 'kind':
      return xs.sort((a, b) => DOCUMENT_KIND[a.kind].localeCompare(DOCUMENT_KIND[b.kind]) || byNew(a, b));
    case 'title':
      return xs.sort((a, b) => a.title.localeCompare(b.title));
  }
}

/**
 * HOW MANY DOCUMENTS A VALUE WOULD LEAVE, counted with every OTHER field's
 * choices applied but not this one's — the same count the backlog's menu wears,
 * so the number answers "how many if I pick this", not "how many in all".
 */
export function docCountFor(field: DocFilterField, value: string, docs: Doc[], f: DocFilter): number {
  const without = { ...f, [field]: [] };
  return docs.filter((d) => docPasses(d, without) && docValue(d, field) === value).length;
}

/** The values a field actually has on this board, with their words, for the menu. */
export function docChoices(
  field: DocFilterField,
  docs: Doc[],
  sprintName: (id: string) => string | undefined
): { value: string; label: string }[] {
  if (field === 'kind') {
    return [...new Set(docs.map((d) => d.kind))].map((k) => ({ value: k, label: DOCUMENT_KIND[k as DocumentKind] }));
  }
  if (field === 'status') {
    /* The states in the order a person works them — the same order the "By
       status" sort uses — not the order the documents happen to arrive in, so
       the menu and the sort never disagree about what comes first. */
    return [...new Set(docs.map((d) => d.state))]
      .sort((a, b) => STATE_ORDER[a] - STATE_ORDER[b])
      .map((s) => ({ value: s, label: DOCUMENT_STATE[s as DocumentState] }));
  }
  return [...new Set(docs.map((d) => d.sprint || COVERS_WHOLE))].map((v) => ({
    value: v,
    label: v === COVERS_WHOLE ? 'Whole project' : sprintName(v) ?? 'A sprint no longer here',
  }));
}
